import React from 'react';

import { Spinner } from '@admin/components/spinner';

import { WEEKDAYS } from './constants';
import { formatDisplayDate, titleize } from './filters_panel';
import { dateInputBounds, formatDateInputValue, parseDateInputValue } from './filters_util';
import { IUnavailability, IUnavailabilityForm, Weekday } from './types';
import { UnavailabilityActions, unavailabilityResource } from './unavailability_actions';

type RecurringTableProps = {
  unavailabilities: IUnavailability[] | null;
  form: IUnavailabilityForm | null;
  selection: IUnavailability | null;
  saving: boolean;
  onAdd(): void;
  onManage(unavailability: IUnavailability): void;
  onDestroy(unavailability: IUnavailability): void;
  onCancel(): void;
  onSave(): void;
  onFormChange(form: IUnavailabilityForm): void;
};

const RecurringRow: React.FC<{
  unavailability?: IUnavailability;
  form: IUnavailabilityForm | null;
  saving: boolean;
  advanced?: boolean;
  onManage(unavailability: IUnavailability): void;
  onDestroy(unavailability: IUnavailability): void;
  onCancel(): void;
  onSave(): void;
  onFormChange(form: IUnavailabilityForm): void;
}> = ({ unavailability, form, saving, advanced = false, onManage, onDestroy, onCancel, onSave, onFormChange }) => {
  const current = unavailabilityResource(unavailability, form);
  const editing = !!form && (!unavailability || unavailability.id === form.id);
  const fieldID = current.id ? String(current.id) : '';
  const { min, max } = dateInputBounds();

  if (advanced) {
    return (
      <tr className="active">
        <td className="col-fill col-nowrap text-left" colSpan={5}>
          <div className="form-inline">
            <strong>Advanced:</strong>
            <span>repeat every week starting</span>

            <div className="form-group form-grouping">
              <input
                name={`unavailability[${fieldID}][start_date]`}
                type="date"
                className="form-control form-control-fit"
                min={min}
                max={max}
                value={formatDateInputValue(
                  current.start_date ? new Date(`${current.start_date}T00:00:00.000Z`) : undefined,
                )}
                onChange={(event) =>
                  onFormChange({
                    ...current,
                    start_date: event.target.value || null,
                  })
                }
              />
            </div>

            <div className="form-group form-grouping">
              <label className="radio-inline">
                <input
                  name={`unavailability[${fieldID}][forever]`}
                  type="radio"
                  value="true"
                  checked={current.forever}
                  onChange={() => onFormChange({ ...current, forever: true, until_date: null })}
                />{' '}
                forever
              </label>
              <label className="radio-inline">
                <input
                  name={`unavailability[${fieldID}][forever]`}
                  type="radio"
                  value="false"
                  checked={!current.forever}
                  onChange={() => onFormChange({ ...current, forever: false, until_date: null })}
                />{' '}
                until
              </label>
            </div>

            <div className="form-group form-grouping">
              <input
                name={`unavailability[${fieldID}][until_date]`}
                type="date"
                className="form-control form-control-fit"
                min={min}
                max={max}
                disabled={current.forever}
                value={formatDateInputValue(
                  current.until_date ? new Date(`${current.until_date}T00:00:00.000Z`) : undefined,
                )}
                onChange={(event) =>
                  onFormChange({
                    ...current,
                    until_date: event.target.value || null,
                  })
                }
              />
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr
      id={unavailability ? `unavailability_${unavailability.id}` : undefined}
      className={editing ? 'active' : undefined}
    >
      <td className="col-nowrap col-nowrap text-left">
        {WEEKDAYS.map((day) => (
          <label key={day} className={`checkbox-inline${editing ? '' : ' disabled'}`}>
            <input
              name={`unavailability[${fieldID}][${day}]`}
              type="checkbox"
              checked={current[day as Weekday]}
              disabled={!editing}
              onChange={(event) =>
                onFormChange({
                  ...current,
                  [day]: event.target.checked,
                })
              }
            />{' '}
            {titleize(day)}
          </label>
        ))}
      </td>

      <td className="col-fill col-nowrap text-left">
        {!editing && (
          <span>
            weekly
            {current.start_date && (
              <span>
                {' '}
                from <strong>{formatDisplayDate(current.start_date)}</strong>
              </span>
            )}
            {current.until_date && (
              <span>
                {' '}
                till <strong>{formatDisplayDate(current.until_date)}</strong>
              </span>
            )}
          </span>
        )}
      </td>

      <td className="col-zero col-nowrap text-right">
        <UnavailabilityActions
          unavailability={unavailability}
          form={form}
          saving={saving}
          onManage={onManage}
          onDestroy={onDestroy}
          onCancel={onCancel}
          onSave={onSave}
        />
      </td>
    </tr>
  );
};

export const RecurringTable: React.FC<RecurringTableProps> = ({
  unavailabilities,
  form,
  selection,
  saving,
  onAdd,
  onManage,
  onDestroy,
  onCancel,
  onSave,
  onFormChange,
}) => {
  const recurring = (unavailabilities ?? []).filter((entry) => entry.reoccurring);
  const showNewForm = !selection && form?.reoccurring;

  return (
    <div className="panel availabilities-reoccurring">
      <div className="panel-heading">
        <div className="panel-title">Reoccurring Conflicts</div>
      </div>
      <div className="table-responsive">
        <table className="table">
          <tbody>
            {recurring.map((unavailability) => (
              <React.Fragment key={unavailability.id}>
                <RecurringRow
                  unavailability={unavailability}
                  form={form}
                  saving={saving}
                  onManage={onManage}
                  onDestroy={onDestroy}
                  onCancel={onCancel}
                  onSave={onSave}
                  onFormChange={onFormChange}
                />
                {selection?.id === unavailability.id && (
                  <RecurringRow
                    unavailability={unavailability}
                    form={form}
                    saving={saving}
                    advanced
                    onManage={onManage}
                    onDestroy={onDestroy}
                    onCancel={onCancel}
                    onSave={onSave}
                    onFormChange={onFormChange}
                  />
                )}
              </React.Fragment>
            ))}
            {showNewForm && (
              <>
                <RecurringRow
                  form={form}
                  saving={saving}
                  onManage={onManage}
                  onDestroy={onDestroy}
                  onCancel={onCancel}
                  onSave={onSave}
                  onFormChange={onFormChange}
                />
                <RecurringRow
                  form={form}
                  saving={saving}
                  advanced
                  onManage={onManage}
                  onDestroy={onDestroy}
                  onCancel={onCancel}
                  onSave={onSave}
                  onFormChange={onFormChange}
                />
              </>
            )}
          </tbody>
        </table>
      </div>
      {!unavailabilities && (
        <div className="panel-content">
          <Spinner />
        </div>
      )}
      <div className="panel-footer">
        <div className="clearfix">
          <button type="button" className="btn btn-default pull-right" onClick={onAdd}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
};
